import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: false,
})

// Request interceptor to add auth token and OU header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add OU code header if available (for OU-scoped requests)
    const activeOU = localStorage.getItem('hris_active_ou')
    if (activeOU && config.headers) {
      config.headers['X-OU-Code'] = activeOU
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    const status = error.response?.status

    if (status === 401) {
      // Skip redirect if we're in the process of logging out
      const isLoggingOut = localStorage.getItem('is_logging_out') === 'true'

      if (!isLoggingOut) {
        // Token expired or invalid, clear storage and redirect to login
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')

        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/auth/login')) {
          window.location.href = '/auth/login'
        }
      }
    } else if (status === 403) {
      // Forbidden - user doesn't have permission
      const responseData = error.response?.data as { message?: string } | undefined
      const message = responseData?.message || 'Access denied'

      // Extract module and permission from error message if available
      // Format: "Unauthorized. Required permission: module.permission"
      const match = message.match(/Required permission: ([^.]+)\.(\w+)/)
      const module = match?.[1] || 'unknown'
      const permission = match?.[2] || 'access'

      // Dispatch custom event for components to handle
      window.dispatchEvent(
        new CustomEvent('api:forbidden', {
          detail: { message, module, permission, url: error.config?.url },
        }),
      )

      // Log for debugging
      console.warn(`[403 Forbidden] ${message}`, { url: error.config?.url })
    }

    return Promise.reject(error)
  },
)

export default apiClient
